import { z } from "zod";
import {
	prepareX6MachineData,
	type X6Machine,
	type X6MachineRequest,
	x6MachineSchema,
} from "@packsize/schemas/machines";

import { packNetAxios } from "@/lib/api-client";
import {
	uiChangeZFoldDataSchema,
	type TrackConfiguration,
	type UiChangeZFoldData,
	type UiTrackConfiguration,
} from "@/schemas";

import { baseRoutes } from "../base-routes";

export async function createX6Machine(data: X6MachineRequest): Promise<void> {
	await packNetAxios.post(`${baseRoutes.x6}/api/v1/machine`, data);
}

export async function updateX6Machine(tenantId: string, machineId: string, data: X6MachineRequest): Promise<void> {
	// TODO @eb: Remove machineId and tenantId from the payload when the backend no longer requires it.
	await packNetAxios.put(`${baseRoutes.x6}/api/v1/machine/${machineId}`, {
		id: machineId,
		tenantId,
		...data,
	});
}

export async function getX6Machines(): Promise<X6Machine[]> {
	const response = await packNetAxios.get(`${baseRoutes.x6}/api/v1/machine`);

	if (Array.isArray(response.data) === false) {
		throw new Error("Unable to fetch X6 machines, response is not a list");
	}

	return z
		.array(x6MachineSchema)
		.enrich({ callSite: "getX6Machines - v2" })
		.parseWithTelemetry(response.data.map(prepareX6MachineData));
}

export async function getX6Machine(machineId: string): Promise<X6Machine | null> {
	const response = await packNetAxios.get(`${baseRoutes.x6}/api/v1/machine/${machineId}`);

	if (response.status !== 200) {
		return null;
	}

	return x6MachineSchema
		.enrich({ callSite: "getX6Machine - v2" })
		.parseWithTelemetry(prepareX6MachineData(response.data));
}

export async function getX6ChangeCorrugateData(machineId: string): Promise<UiChangeZFoldData> {
	const response = await packNetAxios.get(`${baseRoutes.x6}/api/v1/machine/${machineId}/changezfolddata`);
	return uiChangeZFoldDataSchema
		.enrich({ callSite: "getX6ChangeCorrugateData - v2" })
		.parseWithTelemetry(response.data);
}

export async function updateX6Tracks(machineId: string, tracks: UiTrackConfiguration[]): Promise<UiChangeZFoldData> {
	const trackConfigurations: TrackConfiguration[] = tracks.map(({ trackNumber, loadedZFold }) => ({
		trackNumber,
		...(loadedZFold === undefined ? {} : { loadedZFold }),
	}));
	const response = await packNetAxios.put<UiChangeZFoldData>(
		`${baseRoutes.x6}/api/v1/machine/${machineId}/trackconfigurations`,
		trackConfigurations,
	);

	return response.data;
}
